import React from 'react';
import { Button, Dialog, Group, Icon, Input, Tooltip } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useField, useForm } from 'react-final-form';
import { Link } from 'react-router-dom';
import { Field } from '~/app/shared/components/Form';
import PaymentLinkJournalDropdown from '~/app/pages/market/onboarding/CustomerPayments/components/PaymentLinkJournalDropdown';
import { BankAccountDropdown } from '~/app/shared/components/ui/BankAccountDropdown';
import { AccountFromCoADropdown } from '~/app/shared/components/ui/AccountFromCoADropdown';
import { AccountTypeNumber } from '~/types';

interface Props {
    prevStep: () => void;
}

const PaymentAndAccountingSettings = ({ prevStep }: Props) => {
    const { submit } = useForm();
    const fieldBankAccount = useField('bankAccount');
    const fieldIban = useField('iban');
    const fieldJournal = useField('journal');

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        fieldBankAccount.input.onBlur();
        fieldIban.input.onBlur();
        fieldJournal.input.onBlur();

        if (fieldBankAccount.meta.invalid || fieldIban.meta.invalid || fieldJournal.meta.invalid) {
            return;
        }

        // Invokes handleSubmit() from index.tsx
        submit();
    };

    const validateBankAccount = (value: string | number) => (!value ? t`Bank account is required` : undefined);

    const validateIban = (value: string) =>
        /^([a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16})$/g.test(value) ? undefined : t`IBAN number is required`;

    return (
        <>
            <Dialog.Title>
                <Trans>Payment and accounting settings</Trans>
            </Dialog.Title>

            <div className="flex mt-4">
                <Trans>Choose settings for customer payments via the payment link below</Trans>
            </div>

            <div className="flex mt-6">
                <Field
                    required
                    fieldClassName="w-full"
                    name="paymentAccount"
                    label={t`Bank account for payments`}
                    as={BankAccountDropdown}
                    validate={validateBankAccount}
                />
            </div>

            <div className="flex mt-2 justify-end">
                <Link to="/settings/company/bank-accounts">
                    <Trans>Add bank account</Trans> <Icon className="text-xs" name="undock" />
                </Link>
            </div>

            <div className="flex mt-1">
                <Field
                    required
                    fieldClassName="w-full"
                    label={t`IBAN number for payment`}
                    name="bankIban"
                    validate={validateIban}>
                    {({ input, meta: { touched, error } }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>IBAN number for payouts</Trans> *
                                <Tooltip
                                    title={t`IBAN is required for you to receive payments in all currencies. You can find the IBAN number in your online banking on the bank account to which you would like to receive payments.`}>
                                    <Icon
                                        name="info"
                                        className="yt-blue-solid ml-2 rounded-full text-white !h-4 !w-4 !p-0 !ml-1"
                                    />
                                </Tooltip>
                            </span>
                            <Input invalid={error && touched} {...input} />
                            {error && touched && (
                                <span className="h-4 text-xs text-left leading-normal font-normal truncate -mb-4 text-red-500">
                                    {error}
                                </span>
                            )}
                        </label>
                    )}
                </Field>
            </div>

            <div className="flex mt-6">
                <Field fieldClassName="w-full" name="daybook">
                    {({ input }) => (
                        <label className="flex flex-col font-bold text-xs leading-loose pb-4 min-h-[theme(spacing.18)]">
                            <span className="flex items-center">
                                <Trans>Journal for customer payments</Trans>
                                <Tooltip title={t`All entry types must be allowed in the selected journal`}>
                                    <Icon name="info" className="yt-blue-solid rounded-full text-white !h-4 !w-4 !p-0 !ml-1" />
                                </Tooltip>
                            </span>
                            <PaymentLinkJournalDropdown {...input} />
                        </label>
                    )}
                </Field>
            </div>

            <div className="flex mt-6">
                <Field
                    fieldClassName="w-full"
                    name="feeAccount"
                    label={t`Account for payment fees`}
                    as={AccountFromCoADropdown}
                    validate={validateBankAccount}
                    filterByType={AccountTypeNumber.ProfitAndLoss}
                />
            </div>

            <Group className="flex mt-4 justify-end">
                <Button onClick={onPrevClick}>
                    <Trans>Back</Trans>
                </Button>
                <Button appearance="primary" onClick={onNextClick}>
                    <Trans>Next</Trans>
                </Button>
            </Group>
        </>
    );
};

export default PaymentAndAccountingSettings;
